var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import getAsWholeNumberWhenPossible from './whole-number';
const isProd = process.env.NODE_ENV === 'production';
/**
 * G11N-1704 - Depending on the passed options, we have to generate our
 * INTL number in a few different ways. That means we need one function
 * referenced by all "danger" functions.
 *
 * @param {number} number The number to internationalize
 * @param {object} options The options for this number
 */
export const getFinalNumberFromOptions = (locale, number, options) => {
    // Saving this option's value then removing it from the larger object
    const { encourageWholeNumbers } = options, finalOpts = __rest(options, ["encourageWholeNumbers"]);
    // Create the NF object, which could be used in a few different ways
    const nfObj = new Intl.NumberFormat(locale, finalOpts);
    // We were told to return whole numbers
    if (encourageWholeNumbers) {
        // The function is available
        if (typeof nfObj.formatToParts === 'function') {
            return getAsWholeNumberWhenPossible(nfObj, number);
        }
        if (!isProd) {
            // Send a warning that we can't display whole numbers
            // eslint-disable-next-line no-console
            console.warn('Can\'t generate a whole number as NumberFormat.formatToParts() is unavailable');
        }
    }
    return nfObj.format(number);
};
/**
 * Request in G11N-1676. Research and approval documentation located at:
 * https://confluence.nike.com/pages/viewpage.action?pageId=314412833
 */
export const dangerouslyOverrideEnBgBgnCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = 'лв.';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('en', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
* Request in G11N-1675. Research and approval documentation located at:
* https://confluence.nike.com/pages/viewpage.action?pageId=314411721
*/
export const dangerouslyOverrideEnHrHrkCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = 'kn';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('en', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
 * Requested as part of G11N-1466. The reason this overwrite is was
 * deemed "okay" by the team is because en-ID is not an ICU supported language, but
 * is none-the-less one that Nike needs to use for English speakers in Indonesia.
 *
 * The overwrite here needs to use the Indonesian current symbol (Rp) but with
 * US number separators. The native Intl.NumberFormat function does not have the
 * ability to achieve this, therefore we must make an overwrite.
 */
export const dangerouslyOverrideEnIdIdrCurrency = (number, options) => {
    // Enforce certain options so we're guaranteed to get the output we expect
    const finalOpts = Object.assign(Object.assign({}, options), { currencyDisplay: 'symbol' });
    // Get the default output from Intl.NumberFormat
    const finalNumber = getFinalNumberFromOptions('en-ID', number, finalOpts);
    /* numFormat will either be IDR<space>123,456 or IDR<no space>123,456 depending on if
      we're in the browser or using node. The following logic should just replace IDR without
      making a decision on the presence of a space */
    return finalNumber.replace('IDR', 'Rp');
};
/**
* Request in G11N-1683. Research and approval documentation located at:
* https://confluence.nike.com/pages/viewpage.action?pageId=314412950
*/
export const dangerouslyOverrideEnMaMadCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = 'Dh';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('en', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
* Request in G11N-1671. Research and approval documentation located at:
* https://confluence.nike.com/pages/viewpage.action?pageId=313641051
*/
export const dangerouslyOverrideEnNoNokCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = 'kr';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('en', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalSymbol} ${finalNumber}`;
};
/**
* Request in G11N-1675. Research and approval documentation located at:
* https://confluence.nike.com/pages/viewpage.action?pageId=314411721
*/
export const dangerouslyOverrideEnRoRonCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = 'lei';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('en', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
 * Request in G11N-1458.
 * Discussed and approved in subtask G11N-1515.
 *
 * Most Singaporean web experiences have a currency display convention not supported by Intl.NumberFormat.
 * This override modifies the output to match Singaporean expectations around this topic:
 * Currency displays should include an `S` before the `$`; e.g., `S$9,999.00`.
 */
export const dangerouslyOverrideEnSgSgdCurrency = (number, options) => {
    // Enforce certain options so we're guaranteed to get the output we expect
    const finalOpts = Object.assign(Object.assign({}, options), { currencyDisplay: 'symbol' });
    // Get the default output from Intl.NumberFormat and add an `S` in front.
    return `S${getFinalNumberFromOptions('en-SG', number, finalOpts)}`;
};
/**
* Request in G11N-1673. Research and approval documentation located at:
* https://confluence.nike.com/pages/viewpage.action?pageId=313649979
*/
export const dangerouslyOverrideEnSkEurCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = '€';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('en', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
 * Request in G11N-4083
 */
export const dangerouslyOverrideHuHuEurCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = '€';
    // Our research determined that in this case we want English-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('hu-HU', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
 * Request in G11N-1458.
 * Discussed and approved in subtask G11N-1516.
 *
 * Vietnamese web experiences have a currency display convention of placing the `₫` after the number.
 * This override function modifies the output to match Vietnamese expectations around this issue:
 * Currency displays should be something like `9,999.00₫`.
 */
export const dangerouslyOverrideEnVnVndCurrency = (number, options) => {
    // Enforce certain options so we're guaranteed to get the output we expect
    const finalOpts = Object.assign(Object.assign({}, options), { currencyDisplay: 'symbol' });
    // Get the default output from Intl.NumberFormat
    const numFormat = getFinalNumberFromOptions('en-VN', number, finalOpts);
    // Get the default output from Intl.NumberFormat, remove the ₫ at the beginning, and append to end of string.
    return `${numFormat.replace('₫', '')}₫`;
};
/**
* Request in G11N-1681. Research and approval documentation located at:
* https://confluence.nike.com/pages/viewpage.action?pageId=314412929
*/
export const dangerouslyOverrideFrMaMadCurrencyAsSymbol = (number, options) => {
    // We don't want any options passed along other than the one below
    const { encourageWholeNumbers } = options;
    const finalOpts = { encourageWholeNumbers };
    // By the time the code hits this function, we are guaranteed to know
    // that we want this to be the currency symbol to use
    const finalSymbol = 'Dh';
    // Our research determined that in this case we want French-based
    // numbers as part of this output, so generate that as a number (not a currency)
    const finalNumber = getFinalNumberFromOptions('fr-MA', number, finalOpts);
    // Now we merge the two and return, WITH a space
    return `${finalNumber} ${finalSymbol}`;
};
/**
 * Request in G11N-1458.
 */
export const dangerouslyOverrideKoKrCurrencyName = (number, options) => {
    // Enforce certain options so we're guaranteed to get the output we expect
    const finalOpts = Object.assign(Object.assign({}, options), { currencyDisplay: 'name' });
    // Get the default output from Intl.NumberFormat
    const numFormat = getFinalNumberFromOptions('ko', number, finalOpts);
    // Get the default output from Intl.NumberFormat, remove '대한민국 '
    return numFormat.replace('대한민국 ', '');
};
export const dangerouslyOverrideEnInCurrencySymbol = (number, options) => {
    // Enforce certain options so we're guaranteed to get the output we expect
    const finalOpts = Object.assign(Object.assign({}, options), { currencyDisplay: 'symbol' });
    // Get the default output from Intl.NumberFormat
    const numFormat = getFinalNumberFromOptions('en-IN', number, finalOpts);
    // Get the default output from Intl.NumberFormat, remove '대한민국 '
    return numFormat.replace('₹', '₹ ').replace(/,/g, ' ');
};
/**
 * This function generates a number string with the passed args. In
 * very few instances we override the default output with some Nike
 * specific logic.
 *
 * NOTE: Exceptions should NEVER be added without a lot of discussion
 * and string reasoning as to why we should break from the Intl standards
 *
 * @param {string} locale The BCP-47 locale
 * @param {number} number The number to display
 * @param {object} options The Intl.NumberFormat options
 */
export const applyNumberFormatWithDangerousOverrides = (locale, number, options) => {
    const { style, currency, currencyDisplay } = options;
    if (locale === 'en-BG' && style === 'currency' && currency === 'BGN'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnBgBgnCurrencyAsSymbol(number, options);
    }
    if (locale === 'en-HR' && style === 'currency' && currency === 'HRK'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnHrHrkCurrencyAsSymbol(number, options);
    }
    if (locale === 'en-ID' && style === 'currency' && currency === 'IDR') {
        return dangerouslyOverrideEnIdIdrCurrency(number, options);
    }
    if (locale === 'en-MA' && style === 'currency' && currency === 'MAD'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnMaMadCurrencyAsSymbol(number, options);
    }
    if (locale === 'en-NO' && style === 'currency' && currency === 'NOK'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnNoNokCurrencyAsSymbol(number, options);
    }
    if (locale === 'en-RO' && style === 'currency' && currency === 'RON'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnRoRonCurrencyAsSymbol(number, options);
    }
    if (locale === 'en-SG' && style === 'currency' && currency === 'SGD') {
        return dangerouslyOverrideEnSgSgdCurrency(number, options);
    }
    if (locale === 'en-SK' && style === 'currency' && currency === 'EUR'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnSkEurCurrencyAsSymbol(number, options);
    }
    if (locale === 'en-VN' && style === 'currency' && currency === 'VND') {
        return dangerouslyOverrideEnVnVndCurrency(number, options);
    }
    if (locale === 'fr-MA' && style === 'currency' && currency === 'MAD'
        && (currencyDisplay && currencyDisplay === 'symbol')) {
        return dangerouslyOverrideFrMaMadCurrencyAsSymbol(number, options);
    }
    if (locale === 'hu-HU' && style === 'currency' && currency === 'EUR'
        && currencyDisplay === 'symbol') {
        return dangerouslyOverrideHuHuEurCurrencyAsSymbol(number, options);
    }
    if ((locale === 'ko' || locale === 'ko-KR') && style === 'currency' && currency === 'KRW'
        && (!currencyDisplay || currencyDisplay === 'name')) {
        return dangerouslyOverrideKoKrCurrencyName(number, options);
    }
    if (locale === 'en-IN' && style === 'currency' && currency === 'INR'
        && (!currencyDisplay || currencyDisplay === 'symbol')) {
        return dangerouslyOverrideEnInCurrencySymbol(number, options);
    }
    return getFinalNumberFromOptions(locale, number, options);
};
export default applyNumberFormatWithDangerousOverrides;
//# sourceMappingURL=dangerous-overrides.js.map